import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Plus, ArrowLeft } from "lucide-react";
import { useServiceMenu } from "@/hooks/useServiceMenu";
import ServiceRow from "@/components/admin/ServiceRow";
import AddServiceForm from "@/components/admin/AddServiceForm";

const CATEGORY_ORDER = ["Nails", "Facials", "Tanning", "Makeup", "Hair", "Add-on"];

export default function AdminServices() {
    const { user, loading } = useAuth();
    const { services, drafts, busy, updateDraft, saveOne, toggleActive, remove, addNew } = useServiceMenu();
    const [adding, setAdding] = useState(false);

    const grouped = useMemo(() => {
        const map = new Map();
        services.forEach((s) => {
            if (!map.has(s.category)) map.set(s.category, []);
            map.get(s.category).push(s);
        });
        return CATEGORY_ORDER
            .filter((c) => map.has(c))
            .map((c) => [c, map.get(c)])
            .concat([...map.entries()].filter(([c]) => !CATEGORY_ORDER.includes(c)));
    }, [services]);

    if (loading) return <div className="p-10 text-center text-white/50">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="max-w-xl mx-auto mt-10 p-8 rounded-2xl border border-rose/30 bg-rose/5 text-center">
                <p className="font-serif text-2xl">Admin only</p>
                <p className="text-white/60 mt-2">You don't have access to the service menu editor.</p>
                <Link to="/portal" className="inline-block mt-5 text-rose hover:underline">← Back to portal</Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1100px] mx-auto" data-testid="admin-services-page">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                <div>
                    <Link to="/portal" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mb-2">
                        <ArrowLeft className="w-3 h-3" /> Back to portal
                    </Link>
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Admin</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-1">Service Menu</h1>
                    <p className="mt-2 text-white/55 max-w-xl">
                        Single source of truth for prices and durations. Changes are live the moment you save —
                        the public booking page reads from here.
                    </p>
                </div>
                <button
                    onClick={() => setAdding(true)}
                    data-testid="admin-add-service-btn"
                    className="px-5 py-3 rounded-full bg-gradient-to-r from-gold via-rose to-gold text-ink font-medium flex items-center gap-2 shadow-lg shadow-gold/20 hover:opacity-90 transition"
                >
                    <Plus className="w-4 h-4" /> Add service
                </button>
            </div>

            {adding && (
                <AddServiceForm onAdd={addNew} onCancel={() => setAdding(false)} />
            )}

            <div className="space-y-8">
                {grouped.map(([cat, items]) => (
                    <div key={cat}>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">{cat}</p>
                        <div className="rounded-2xl border border-white/10 bg-surface overflow-hidden">
                            {items.map((s) => (
                                <ServiceRow
                                    key={s.service_id}
                                    service={s}
                                    draft={drafts[s.service_id] || { price: "", duration_min: 0, name: s.name }}
                                    busy={!!busy[s.service_id]}
                                    onDraft={updateDraft}
                                    onSave={saveOne}
                                    onToggle={toggleActive}
                                    onRemove={remove}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <p className="mt-10 text-xs text-white/40 text-center">
                The booking page reads from this menu in real time. Emergency upcharge is currently <span className="text-rose">$75</span> (set in code).
            </p>
        </div>
    );
}
